/**
 * Maintenance Mode Page
 *
 * Displays a professional maintenance message with proper HTTP 503 status.
 * Follows best practices from vision/maintenance.md:
 * - Transparency about why and when
 * - Minimal but professional design
 * - Auto-refresh capability
 * - Alternative contact channels
 */

import { useEffect } from 'react';
import { MAINTENANCE_MODE } from '../lib/config';

export default function MaintenancePage() {
  // Format the return date for display
  const getFormattedReturnDate = () => {
    if (!MAINTENANCE_MODE.estimatedReturn) return null;

    const returnDate = new Date(MAINTENANCE_MODE.estimatedReturn);
    return returnDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Auto-refresh every 60 seconds to check if maintenance is over
  useEffect(() => {
    const interval = setInterval(() => {
      // Simple fetch to check if we're still in maintenance
      fetch(window.location.href, { method: 'HEAD' })
        .then((response) => {
          if (response.status !== 503) {
            window.location.reload();
          }
        })
        .catch(() => {
          // Ignore errors, we'll try again in 60s
        });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="max-w-md text-center">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-white p-4 shadow-lg">
            <svg
              className="h-12 w-12 text-gray-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <h1 className="mb-3 text-3xl font-semibold text-gray-900">Scheduled Maintenance</h1>

        {/* Reason */}
        <p className="mb-4 text-lg text-gray-700">
          {MAINTENANCE_MODE.reason ||
            "We're currently performing system upgrades to improve your experience."}
        </p>

        {/* Estimated return */}
        {MAINTENANCE_MODE.estimatedReturn && (
          <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-600">Expected to return</p>
            <p className="text-xl font-bold text-gray-900">{getFormattedReturnDate()}</p>
          </div>
        )}

        {/* Description */}
        <p className="mb-6 text-gray-600">
          We apologize for any inconvenience. Our service will be back online shortly.
        </p>

        {/* Auto-refresh notice */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <svg className="h-4 w-4 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>This page will automatically refresh when we're back</span>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-8 text-xs text-gray-500">
          This is a temporary interruption. Thank you for your patience.
        </p>
      </div>
    </div>
  );
}
